import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, mapTo } from 'rxjs/operators';
import { PagedResponse } from '../core';
import {
  GetWholesaleProviderPublicKeyRequest,
  ListPaymentRequest,
  ListRetailDisputesRequest,
  ListRetailDisputesRequestListRetailDisputesFilters,
  ListRetailDisputesResponse,
  ListRetailPaymentsRequest,
  ListRetailPaymentsRequestListRetailPaymentsFilters,
  ListRetailPaymentsResponse,
  ListRetailTransactionsRequest,
  ListRetailTransactionsRequestListRetailTransactionsFilters,
  ListRetailTransactionsResponse,
  PagedRequestOptions,
  PaymentApiService,
  PrepareRetailPaymentRequest,
  RetailPaymentsEnabledRequest,
  RetailStatusRequest,
  RetailStatusResponse,
  RetailTransactionType,
} from '@vendasta/billing';
import { Dispute, DisputeEvidenceRequest, disputeFromApi } from './dispute';
import { Intent, ListPaymentsFilter, Payment, paymentFromApi, RetailProvider } from './payment';
import {
  RetailPayment,
  retailPaymentFromApi,
  RetailRefundReason,
  retailRefundReasonToApi,
  RetailTransaction,
  retailTransactionFromApi,
} from './retail-payment';
import { retailResponseFromAPI, RetailStatus } from './retail-status';
import { CountryProviderDeterminant, MerchantProviderDeterminant } from './provider-determinant';
import {
  PaymentFacilitatorType,
  paymentFacilitatorTypeFromApi,
  paymentFacilitatorTypeToApi,
} from './payment-facilitator-type';
import { DisputeStatus as DisputeStatusApi } from '@vendasta/billing/lib/_internal/enums/payment.enum';

@Injectable({ providedIn: 'root' })
export class PaymentService {
  constructor(private paymentApi: PaymentApiService) {}

  listPayments(filters: ListPaymentsFilter, cursor: string, pageSize: number): Observable<PagedResponse<Payment>> {
    const req = new ListPaymentRequest({
      filters: filters,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.paymentApi.list(req).pipe(
      map((response) => {
        const payments = (response.payments || []).map((payment) => paymentFromApi(payment));
        return new PagedResponse(payments, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  prepareRetailPayment(merchantId: string, customerId: string): Observable<Intent> {
    const req = new PrepareRetailPaymentRequest({
      merchantId: merchantId,
      customerId: customerId,
    });
    return this.paymentApi.prepareRetail(req).pipe(
      map((response) => {
        return {
          stripeClientSecret: response.stripeClientSecret,
          intentId: response.intentId,
        };
      }),
    );
  }

  getRetail(merchantId: string, paymentId: string): Observable<RetailPayment> {
    return this.paymentApi
      .getRetail({
        merchantId: merchantId,
        paymentId: paymentId,
      })
      .pipe(map((resp) => retailPaymentFromApi(resp.payment)));
  }

  listRetail(
    merchantId: string,
    cursor = '',
    pageSize = 10,
    payoutId?: string,
    customerId?: string,
    createdDateGte?: Date,
    createdDateLte?: Date,
  ): Observable<PagedResponse<RetailPayment>> {
    const zeroDate = new Date('0001-01-01T00:00:00Z').valueOf();
    const filters: ListRetailPaymentsRequestListRetailPaymentsFilters =
      new ListRetailPaymentsRequestListRetailPaymentsFilters({
        payoutId: payoutId || '',
      });
    if (!!createdDateGte && createdDateGte.valueOf() !== zeroDate) {
      filters.createdDateGte = createdDateGte;
    }
    if (!!createdDateLte && createdDateLte.valueOf() !== zeroDate) {
      filters.createdDateLte = createdDateLte;
    }
    return this.paymentApi
      .listRetail(
        new ListRetailPaymentsRequest({
          merchantId: merchantId,
          filters: filters,
          pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
        }),
      )
      .pipe(
        map((resp: ListRetailPaymentsResponse) => {
          const retailPayments = (resp.payments || []).map((payment) => retailPaymentFromApi(payment));
          return new PagedResponse(retailPayments, resp.pagingMetadata.nextCursor, resp.pagingMetadata.hasMore);
        }),
      );
  }

  listRetailTransactions(
    merchantId: string,
    cursor = '',
    pageSize = 10,
    payoutId?: string,
    createdDateGte?: Date,
    createdDateLte?: Date,
    paymentsOnly?: boolean,
    customerId?: string,
    invoiceId?: string,
  ): Observable<PagedResponse<RetailTransaction>> {
    const zeroDate = new Date('0001-01-01T00:00:00Z').valueOf();
    const filters: ListRetailTransactionsRequestListRetailTransactionsFilters =
      new ListRetailTransactionsRequestListRetailTransactionsFilters({
        payoutId: payoutId || '',
      });
    if (!!createdDateGte && createdDateGte.valueOf() !== zeroDate) {
      filters.createdDateGte = createdDateGte;
    }
    if (!!createdDateLte && createdDateLte.valueOf() !== zeroDate) {
      filters.createdDateLte = createdDateLte;
    }
    if (paymentsOnly === undefined || paymentsOnly) {
      filters.type = RetailTransactionType.RETAIL_TRANSACTION_TYPE_PAYMENT;
    }
    if (customerId) {
      filters.customerId = customerId;
    }
    if (invoiceId) {
      filters.invoiceId = invoiceId;
    }
    return this.paymentApi
      .listRetailTransactions(
        new ListRetailTransactionsRequest({
          merchantId: merchantId,
          filters: filters,
          pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
        }),
      )
      .pipe(
        map((resp: ListRetailTransactionsResponse) => {
          const retailTransactions = (resp.transactions || []).map((transaction) =>
            retailTransactionFromApi(transaction),
          );
          return new PagedResponse(retailTransactions, resp.pagingMetadata.nextCursor, resp.pagingMetadata.hasMore);
        }),
      );
  }

  getRetailDispute(merchantId: string, disputeId: string): Observable<Dispute | undefined> {
    return this.paymentApi
      .getRetailDispute({
        merchantId: merchantId,
        disputeId: disputeId,
      })
      .pipe(map((resp) => disputeFromApi(resp.dispute)));
  }

  listRetailDisputes(
    merchantId: string,
    cursor = '',
    pageSize = 10,
    status?: DisputeStatusApi,
  ): Observable<PagedResponse<Dispute | undefined>> {
    const filters = new ListRetailDisputesRequestListRetailDisputesFilters({
      status: status,
    });
    return this.paymentApi
      .listRetailDisputes(
        new ListRetailDisputesRequest({
          merchantId: merchantId,
          pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
          filters: filters,
        }),
      )
      .pipe(
        map((resp: ListRetailDisputesResponse) => {
          const retailPayments = (resp?.disputes || []).map((d) => disputeFromApi(d));
          return new PagedResponse(
            retailPayments,
            resp?.pagingMetadata.nextCursor || '',
            resp?.pagingMetadata.hasMore || false,
          );
        }),
      );
  }

  retailStatus(merchantId: string): Observable<RetailStatus> {
    const req = new RetailStatusRequest({ merchantId });
    return this.paymentApi.retailStatus(req).pipe(map((r: RetailStatusResponse) => retailResponseFromAPI(r)));
  }

  retailPaymentsEnabled(merchantId: string): Observable<boolean> {
    const req = new RetailPaymentsEnabledRequest({ merchantId });
    return this.paymentApi.retailPaymentsEnabled(req).pipe(map((resp) => resp.enabled));
  }

  configureRetailProvider(
    merchantId: string,
    successUrl: string,
    failureUrl: string,
    pft?: PaymentFacilitatorType,
  ): Observable<string> {
    const paymentFacilitatorType = paymentFacilitatorTypeToApi(pft || PaymentFacilitatorType.NOT_SET);
    return this.paymentApi
      .configureRetailProvider({
        merchantId,
        successUrl,
        failureUrl,
        paymentFacilitatorType,
      })
      .pipe(map((resp) => resp.stripeUrl));
  }

  getRetailProvider(merchantId: string): Observable<RetailProvider> {
    return this.paymentApi.getRetailProvider({ merchantId }).pipe(
      map((resp) => {
        return {
          statementDescriptor: resp.statementDescriptor,
          stripeConnectId: resp.stripeConnectId,
          paymentFacilitatorType: paymentFacilitatorTypeFromApi(resp.paymentFacilitatorType),
          countryCode: resp.countryCode,
        };
      }),
    );
  }

  updateRetailProvider(merchantId: string, statementDescriptor: string): Observable<null> {
    return this.paymentApi.updateRetailProvider({ merchantId, statementDescriptor }).pipe(mapTo(null));
  }

  submitEvidence(
    merchantId: string,
    disputeId: string,
    paymentId: string,
    submit: boolean,
    evidence?: DisputeEvidenceRequest,
  ): Observable<null> {
    return this.paymentApi
      .submitEvidence({
        merchantId: merchantId,
        disputeId: disputeId,
        paymentId: paymentId,
        evidence: {
          billingAddress: evidence?.billingAddress,
          customerEmailAddress: evidence?.customerEmailAddress,
          customerName: evidence?.customerName,
          productDescription: evidence?.productDescription,
          customerSignatureFileId: evidence?.customerSignatureFileId,
          customerCommunicationFileId: evidence?.customerCommunicationFileId,
          receiptFileId: evidence?.receiptFileId,
          shippingDocumentationFileId: evidence?.shippingDocumentationFileId,
          uncategorizedFileId: evidence?.uncategorizedFileId,
        },
        submit: submit,
      })
      .pipe(mapTo(null));
  }

  closeDispute(merchantId: string, disputeId: string): Observable<null> {
    return this.paymentApi
      .closeRetailDispute({
        merchantId: merchantId,
        disputeId: disputeId,
      })
      .pipe(mapTo(null));
  }

  getWholesaleProviderPublicKey(
    determinant: MerchantProviderDeterminant | CountryProviderDeterminant,
  ): Observable<string> {
    let request: GetWholesaleProviderPublicKeyRequest;
    if (determinant instanceof MerchantProviderDeterminant) {
      request = new GetWholesaleProviderPublicKeyRequest({ merchantId: determinant.merchantId });
    } else {
      request = new GetWholesaleProviderPublicKeyRequest({ countryCode: determinant.countryCode });
    }
    return this.paymentApi.getWholesaleProviderPublicKey(request).pipe(map((resp) => resp.publcApiKey));
  }

  refundRetail(
    merchantId: string,
    paymentId: string,
    amount: number,
    reason: RetailRefundReason,
  ): Observable<RetailPayment> {
    return this.paymentApi
      .refundRetail({ merchantId, paymentId, amount, reason: retailRefundReasonToApi(reason) })
      .pipe(map((resp) => retailPaymentFromApi(resp.payment)));
  }
}
